<template>
    <div class="page-content">
        <UserHeadline title="WatchList.title" description="WatchList.description" />
        <div class="page-content__container">
            <WatchListSelection
                :watchlists="watchLists"
                v-on:selectWatchlist="onSelectWatchlist"
                v-on:createNewWatchlist="onCreateNewWatchlist"
                v-on:removeWatchlist="onRemoveWatchlist"
                v-on:openShareWatchListMenu="onOpenShareWatchListMenu"
                v-on:hideOrShowWatchList="onHideOrShowWatchList"
                v-on:updateSharedWatchList="onUpdateSharedWatchList"
                v-model="showCompleted"
                ref="watchlistsController"
                :editable="editData"
            />
            <WatchListUpgradedCollectionsBanner v-if="shouldShowUpgradedCollectionBanner" />
            <div class="watchlist-result">
                <WatchListControllsAndDetails
                    v-if="reactiveSelectedWatchList"
                    :watchlist="reactiveSelectedWatchList"
                    v-on:editData="onEditData"
                    v-on:saveData="onSaveData"
                    v-on:removeElements="onRemoveElements"
                    v-on:toggleExpiredStudies="onToggleExpiredStudies"
                    ref="watchlistDetails"
                    :editable="editData"
                />
                <StudyPlanLearningPaths
                    :editable="editData"
                    :watchlist="reactiveSelectedWatchList"
                    :showCompleted="showCompleted"
                    :showExpiredStudies="showExpiredStudies"
                    ref="watchlistLearningPaths"
                />
                <WatchListCourses
                    :editable="editData"
                    :watchlist="reactiveSelectedWatchList"
                    :showCompleted="showCompleted"
                    :showExpiredStudies="showExpiredStudies"
                    ref="watchlistCourses"
                />
                <WatchListCollections
                    :editable="editData"
                    :watchlist="reactiveSelectedWatchList"
                    :showCompleted="showCompleted"
                    :showExpiredStudies="showExpiredStudies"
                    ref="watchlistCollections"
                />
            </div>
        </div>
        <ModalAddNewWatchlist v-on:new-watchlist-added="onNewWatchlistCreated"/>
        <NoWatchListsYetModal />
        <ShareWatchListSelectUsersModal
            v-on:shareWithSelectedSubordinates="onShareWithSelectedSubordinates"
            v-on:removeSelectedSubordinates="onRemoveSelectedSubordinates"
            :selectedWatchList="selectedWatchList"
        />
        <ShareWatchListTypeModal
            v-on:shareWithAllSubordinates="onShareWithAllSubordinates"
            v-on:removeSharingWithAllSubordinates="onRemoveSharingWithAllSubordinates"
        />
        <ShareWatchListUserListModal v-on:shareWithAllSubordinatesAccepted="onShareWithAllSubordinatesAccepted" />
    </div>
</template>

<script>
    import WatchListSelection from 'components/user/watchlist/WatchListSelection'
    import WatchListCourses from 'components/user/watchlist/courses/WatchListCourses'
    import { mapGetters } from 'vuex'
    import WatchListControllsAndDetails from 'components/user/watchlist/WatchListControllsAndDetails'
    import WatchListCollections from 'components/user/watchlist/collections/WatchListCollections'
    import ModalAddNewWatchlist from 'components/shared/modals/ModalAddNewWatchlist'
    import UserHeadline from 'components/user/UserHeadline'
    import ShareWatchListSelectUsersModal from 'components/user/watchlist/sharewatchlist/selectusers/ShareWatchListSelectUsersModal'
    import ShareWatchListTypeModal from 'components/user/watchlist/sharewatchlist/selectsharetype/ShareWatchListTypeModal'
    import ShareWatchListUserListModal from 'components/user/watchlist/sharewatchlist/ShareWatchListUserListModal'
    import NoWatchListsYetModal from 'components/user/watchlist/NoWatchListsYetModal'
    import StudyPlanLearningPaths from 'components/user/watchlist/learningpaths/StudyPlanLearningPaths'
    import WatchListUpgradedCollectionsBanner from 'components/user/watchlist/WatchListUpgradedCollectionsBanner'
    import moment from 'moment'

    export default {
        components: {
            WatchListCollections,
            WatchListControllsAndDetails,
            WatchListSelection,
            WatchListCourses,
            ModalAddNewWatchlist,
            UserHeadline,
            ShareWatchListSelectUsersModal,
            ShareWatchListTypeModal,
            ShareWatchListUserListModal,
            NoWatchListsYetModal,
            StudyPlanLearningPaths,
            WatchListUpgradedCollectionsBanner
        },
        data () {
            return {
                selectedWatchList: null,
                showCompleted: true,
                editData: false,
                selectedWatchlistHasTransformedLearningPaths: false,
                showExpiredStudies: true
            }
        },
        computed: {
            ...mapGetters({
                watchLists: 'Watchlists/getWatchLists',
                user: 'User/getCurrentUser',
                userId: 'getUserId',
                selectedSubordinates: 'Watchlists/getShareWatchListSelectedSubordinates',
                selectedMembers: 'Watchlists/getShareWatchListSelectedMembers',
                subordinates: 'Watchlists/getSubordinates'
            }),
            reactiveSelectedWatchList () {
                return this.watchLists.find(watchList => +watchList.id === +this.selectedWatchList)
            },
            shouldShowUpgradedCollectionBanner () {
                const showUpgradedCollectionsBanner = window.localStorage.getItem('showWatchListUpgradedCollectionsBanner') || true
                return showUpgradedCollectionsBanner === true && this.selectedWatchlistHasTransformedLearningPaths
            }
        },
        methods: {
            onToggleExpiredStudies (value) {
                this.showExpiredStudies = value
            },
            onNewWatchlistCreated () {
                this.$modal.hide('add-new-watchlist')
                this.$refs.watchlistsController.onWatchlistCreated()
            },
            onCreateNewWatchlist () {
                this.$modal.show('add-new-watchlist')
            },
            onRemoveWatchlist () {
                const text = this.$t('Watchlist.modal_delete_study_plan')
                this.showModal(text, this.onRemoveWatchlistAccepted)
            },
            async onRemoveWatchlistAccepted () {
                await this.removeWatchlist()
                this.$modal.hide('dialog')
                this.$refs.watchlistsController.onWatchlistRemoved()
            },
            onOpenShareWatchListMenu () {
                this.$modal.show('share-watchlist-type-menu')
            },
            onShareWithAllSubordinates () {
                this.$modal.show('share-watchlist-user-list')
            },
            onRemoveSharingWithAllSubordinates () {
                const text = this.$t('Watchlist.modal_share_study_plan_remove_share_all')
                this.showModal(text, this.onRemoveSharingWithAllSubordinatesAccepted)
            },
            onRemoveSelectedSubordinates () {
                const textStart = this.$t('Watchlist.modal_share_study_plan_remove_subordinates_start')
                const textEnd = this.$t('Watchlist.modal_share_study_plan_remove_subordinates_end')
                const modalText = textStart + ' ' + this.selectedMembers.length + ' ' + textEnd
                this.showModal(modalText, this.onRemoveSelectedSubordinatesAccepted)
            },
            onShareWithSelectedSubordinates () {
                const textStart = this.$t('WatchListModal.share_watchlist_confirm_selected_user_share_start')
                const textEnd = this.$t('WatchListModal.share_watchlist_confirm_selected_user_share_end')
                const modalText = textStart + ' ' + this.selectedSubordinates.length + ' ' + textEnd
                this.showModal(modalText, this.onShareWithSelectedSubordinatesAccepted)
            },
            onHideOrShowWatchList () {
                let text = this.$t('Watchlist.modal_hide_study_plan')
                if (this.reactiveSelectedWatchList.isHidden) {
                    text = this.$t('Watchlist.modal_show_study_plan')
                }
                this.showModal(text, this.onHideWatchlistAccepted)
            },
            async onShareWithAllSubordinatesAccepted () {
                await this.shareWatchListToAllSubordinates(this.userId)
            },
            async onRemoveSharingWithAllSubordinatesAccepted () {
                await this.removeSharingToAllSubordinates(this.userId)
            },
            async onShareWithSelectedSubordinatesAccepted () {
                await this.shareWatchListToSelectedUsers(this.userId)
            },
            async onRemoveSelectedSubordinatesAccepted () {
                await this.removeSelectedSubordinatesFromWatchList(this.userId)
                this.resetShareWatchListSelectUsersModal()
            },
            async resetShareWatchListSelectUsersModal () {
                await this.$store.dispatch('Watchlists/shareWatchListChangeTab', 1)
                await this.$store.dispatch('Watchlists/shareWatchListAddAllSubordinates', [])
                await this.$store.dispatch('Watchlists/shareWatchListAddAllMembers', [])
                await this.$store.dispatch('Watchlists/selectTeam', null)
            },
            onUpdateSharedWatchList () {
                const text = this.$t('Watchlist.modal_update_shared_study_plan')
                this.showModal(text, this.onUpdateWatchlistAccepted)
            },
            async onUpdateWatchlistAccepted () {
                await this.shareWatchListToAllSubordinates(this.userId)
                this.$modal.hide('dialog')
                this.$refs.watchlistsController.onUpdateSharedWatchListUpdated()
            },
            async onHideWatchlistAccepted () {
                this.$modal.hide('dialog')
                this.reactiveSelectedWatchList.isHidden = !this.reactiveSelectedWatchList.isHidden
                await this.onSaveData()
                if ((this.selectedWatchList.isShared && !this.selectedWatchList.isReadonly)) {
                    await this.shareWatchListToAllSubordinates(this.userId)
                }
            },
            showModal (mainText, onYes) {
                this.$modal.show('dialog', {
                    text: mainText,
                    buttons: [
                        {
                            title: this.$t('General.no'),
                            handler: () => {
                                this.$modal.hide('dialog')
                            }
                        },
                        {
                            title: this.$t('General.yes'),
                            default: true,
                            handler: onYes
                        }
                    ]
                })
            },
            onSelectWatchlist (selectedWatchList) {
                this.selectedWatchList = selectedWatchList
                let watchlist = this.watchLists.find(watchList => +watchList.id === selectedWatchList)
                let learningPaths = watchlist.learningPaths

                if (learningPaths.length) {
                    let transformedLearningPaths = learningPaths.filter(item => item.collectionId !== null)
                    let learningPathsTransformedAfterWatchlistCreation = transformedLearningPaths.filter(item => {
                        return watchlist.sharedAt !== null
                            ? moment(watchlist.sharedAt) < moment(item.migratedAt)
                            : moment(watchlist.createdAt) < moment(item.migratedAt)
                    })
                    learningPathsTransformedAfterWatchlistCreation.length > 0
                        ? this.selectedWatchlistHasTransformedLearningPaths = true
                        : this.selectedWatchlistHasTransformedLearningPaths = false
                } else {
                    this.selectedWatchlistHasTransformedLearningPaths = false
                }
            },
            onEditData () {
                this.editData = true
            },
            async removeWatchlist () {
                if (this.selectedWatchList) {
                    return this.$store.dispatch('Watchlists/deleteWatchList', this.selectedWatchList)
                }
                this.$refs.watchlistsController.unselectWatchlist()
            },
            async shareWatchListToAllSubordinates (userId) {
                this.$modal.hide('share-watchlist-user-list')
                if (this.selectedWatchList) {
                    const data = {
                        watchlistId: this.selectedWatchList,
                        userId: userId,
                        mode: 0
                    }
                    try {
                        await this.$store.dispatch('Watchlists/shareWatchList', data)
                        this.$modal.hide('share-watchlist-type-menu')
                        this.$refs.watchlistsController.onWatchlistShared()
                    } catch (error) {
                        this.sharedWatchListError(this.$t('WatchListModal.error_on_share'), 'shareWatchList')
                    }
                }
                this.$refs.watchlistsController.unselectWatchlist()
            },
            async shareWatchListToSelectedUsers (userId) {
                this.$modal.hide('dialog')
                if (this.selectedWatchList) {
                    const data = {
                        watchlistId: this.selectedWatchList,
                        userId: userId,
                        mode: 1
                    }
                    try {
                        await this.$store.dispatch('Watchlists/shareWatchList', data)
                        this.$modal.hide('share-watchlist-select-menu')
                        this.$refs.watchlistsController.onWatchlistSharedToAmountOfUsers()
                        this.resetShareWatchListSelectUsersModal()
                    } catch (error) {
                        this.sharedWatchListError(this.$t('WatchListModal.error_on_share'), 'shareWatchList')
                    }
                }
                this.$refs.watchlistsController.unselectWatchlist()
            },
            async removeSharingToAllSubordinates (userId) {
                this.$modal.hide('dialog')
                if (this.selectedWatchList) {
                    const data = {
                        watchlistId: this.selectedWatchList,
                        userId: userId,
                        mode: 1
                    }
                    try {
                        await this.$store.dispatch('Watchlists/removeSharingToAllSubordinates', data)
                        this.$modal.hide('share-watchlist-type-menu')
                        this.$refs.watchlistsController.onWatchlistSharingRemovedFromUsers(this.subordinates.length)
                    } catch (error) {
                        this.sharedWatchListError(this.$t('WatchListModal.error_on_removing_share'), 'removeSharing')
                    }
                }
                this.$refs.watchlistsController.unselectWatchlist()
            },
            async removeSelectedSubordinatesFromWatchList (userId) {
                this.$modal.hide('dialog')
                if (this.selectedWatchList) {
                    const data = {
                        watchlistId: this.selectedWatchList,
                        userId: userId,
                        mode: 1
                    }
                    try {
                        await this.$store.dispatch('Watchlists/removeSubordinatesFromSharedWatchList', data)
                        this.$modal.hide('share-watchlist-select-menu')
                        this.$refs.watchlistsController.onWatchlistSharingRemovedFromUsers(this.selectedMembers.length)
                    } catch (error) {
                        this.sharedWatchListError(this.$t('WatchListModal.error_on_removing_share'), 'removeSharing')
                    }
                }
            },
            sharedWatchListError (errorMessage, taskType) {
                this.$store.dispatch('LoaderScreen/removeTaskFromLoader', taskType, { root: true })
                this.$store.dispatch('Watchlists/setShareWatchListErrorMessage', errorMessage)
            },
            deselectWatchlist () {
                this.$refs.watchlistsController.unselectWatchlist()
            },
            async onSaveData () {
                const details = this.$refs.watchlistDetails.getElementsToSave()
                const courses = this.$refs.watchlistCourses.getElementsToSave()
                const learningPaths = this.$refs.watchlistLearningPaths.getElementsToSave()
                const collections = this.$refs.watchlistCollections.getElementsToSave()
                if (this.selectedWatchList) {
                    const data = {
                        id: this.selectedWatchList,
                        ...details,
                        courses,
                        collections,
                        learningPaths
                    }
                    data.isHidden = this.reactiveSelectedWatchList.isHidden
                    try {
                        await this.$store.dispatch('Watchlists/updateWatchList', data)
                        this.$refs.watchlistDetails.onSaveData()
                        this.$refs.watchlistCourses.onSaveData()
                        this.$refs.watchlistCollections.onSaveData()
                        this.$refs.watchlistLearningPaths.onSaveData()
                    } catch (e) {

                    }
                }
                this.$refs.watchlistsController.unselectWatchlist()

                this.editData = false
            },
            onRemoveElements () {
                this.$refs.watchlistCourses.removeElements()
                this.$refs.watchlistCollections.removeElements()
                this.$refs.watchlistLearningPaths.removeElements()
            }
        },
        watch: {
            watchLists: {
                handler () {
                    if (this.watchLists.length === 0) {
                        this.$modal.show('no-watchlists-yet')
                    }
                },
                immediate: true
            }
        },
        created () {
            this.$store.dispatch('Watchlists/loadWatchlists')
        }
    }
</script>

<style lang="scss" scoped>
    .watchlist-result {
        display: flex;
        flex-direction: column;

        [class*="desktop"] & {
            border: 1px solid $quill_gray;
            padding: 40px;
        }
    }

    .icon-info {
        font-size: 2rem;
        margin: 0 0 0 8px;
    }
</style>
